import React, { useEffect, useState } from 'react';
import { Calendar, Clock, User, AlertCircle } from 'lucide-react';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ListWidgetProps {
  config: {
    listType?: string;
  };
  size: WidgetSize;
}

const ListWidget: React.FC<ListWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    loadListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.listType, userProfile?.empresaCnpj]);

  const loadListData = async () => {
    if (!userProfile?.empresaCnpj || !config.listType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [turmas, professores, alunos, mensalidades] = await Promise.all([
        firebaseService.getTurmas(userProfile.empresaCnpj),
        firebaseService.getProfessores(userProfile.empresaCnpj),
        firebaseService.getAlunos(userProfile.empresaCnpj),
        firebaseService.getAllMensalidades(userProfile.empresaCnpj)
      ]);

      switch (config.listType) {
        case 'agenda-hoje':
          setTitle('Agenda de Hoje');
          const hoje = new Date();
          const diaSemana = hoje.getDay();
          const diasSemanaMap: { [key: number]: string } = {
            0: 'Domingo', 1: 'Segunda', 2: 'Terça', 3: 'Quarta',
            4: 'Quinta', 5: 'Sexta', 6: 'Sábado'
          };
          const diaAtual = diasSemanaMap[diaSemana];
          const turmasHoje = turmas
            .filter(t => t.ativo && (typeof t.diasSemana === 'string' 
              ? JSON.parse(t.diasSemana).includes(diaAtual)
              : Array.isArray(t.diasSemana) && t.diasSemana.includes(diaAtual)))
            .map(t => {
              let professoresIds: string[] = [];
              if (typeof t.professoresIds === 'string') {
                try { professoresIds = JSON.parse(t.professoresIds); } catch {}
              } else if (Array.isArray(t.professoresIds)) {
                professoresIds = t.professoresIds;
              }
              let alunosIds: string[] = [];
              if (typeof t.alunosIds === 'string') {
                try { alunosIds = JSON.parse(t.alunosIds); } catch {}
              } else if (Array.isArray(t.alunosIds)) {
                alunosIds = t.alunosIds;
              }
              return {
                id: t.id,
                nome: t.nome,
                horario: `${t.horarioInicio || ''} - ${t.horarioFim || ''}`,
                professores: professoresIds.map(id => professores.find(p => p.id === id)?.nome).filter(Boolean).join(', '),
                alunos: alunosIds.length,
                limite: t.limiteAlunos || 0
              };
            })
            .sort((a, b) => (a.horario || '').localeCompare(b.horario || ''));
          setItems(turmasHoje);
          break;
        case 'proximos-vencimentos':
          setTitle('Próximos Vencimentos');
          const hoje2 = new Date();
          const proximos7Dias = new Date(hoje2.getTime() + 7 * 24 * 60 * 60 * 1000);
          const vencimentos = mensalidades
            .filter(m => {
              const vencimento = new Date(m.dataVencimento);
              return vencimento >= hoje2 && vencimento <= proximos7Dias && 
                     (!m.dataPagamento || m.dataPagamento === '');
            })
            .slice(0, 10)
            .map(m => {
              const aluno = alunos.find(a => a.id === m.alunoId);
              return {
                id: m.id,
                aluno: aluno?.nome || 'Não encontrado',
                valor: m.valor,
                vencimento: format(new Date(m.dataVencimento), 'dd/MM/yyyy', { locale: ptBR })
              };
            });
          setItems(vencimentos);
          break;
        case 'alunos-recentes':
          setTitle('Alunos Recentes');
          const recentes = alunos
            .filter(a => a.bAtivo)
            .sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime())
            .slice(0, 10)
            .map(a => ({
              id: a.id,
              nome: a.nome,
              dataCadastro: format(new Date(a.createDate), 'dd/MM/yyyy', { locale: ptBR })
            }));
          setItems(recentes);
          break;
        case 'top-5-turmas':
          setTitle('Top 5 Turmas');
          const topTurmas = turmas
            .filter(t => t.ativo)
            .map(t => {
              let alunosIds: string[] = [];
              if (typeof t.alunosIds === 'string') {
                try { alunosIds = JSON.parse(t.alunosIds); } catch {}
              } else if (Array.isArray(t.alunosIds)) {
                alunosIds = t.alunosIds;
              }
              return {
                id: t.id,
                nome: t.nome,
                ocupacao: t.limiteAlunos > 0 ? Math.round((alunosIds.length / t.limiteAlunos) * 100) : 0,
                alunos: alunosIds.length,
                limite: t.limiteAlunos
              };
            })
            .sort((a, b) => b.ocupacao - a.ocupacao)
            .slice(0, 5);
          setItems(topTurmas);
          break;
        case 'mensalidades-vencidas':
          setTitle('Mensalidades Vencidas');
          const hoje3 = new Date();
          const vencidas = mensalidades
            .filter(m => {
              const vencimento = new Date(m.dataVencimento);
              return vencimento < hoje3 && (!m.dataPagamento || m.dataPagamento === '');
            })
            .slice(0, 10)
            .map(m => {
              const aluno = alunos.find(a => a.id === m.alunoId);
              return {
                id: m.id,
                aluno: aluno?.nome || 'Não encontrado',
                valor: m.valor,
                vencimento: format(new Date(m.dataVencimento), 'dd/MM/yyyy', { locale: ptBR }),
                diasAtraso: Math.floor((hoje3.getTime() - new Date(m.dataVencimento).getTime()) / (1000 * 60 * 60 * 24))
              };
            });
          setItems(vencidas);
          break;
        default:
          setTitle('Lista');
          setItems([]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados da lista:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const maxItems = size === 'small' ? 3 : size === 'medium' ? 5 : 10;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {items.slice(0, maxItems).map((item, index) => (
          <div key={item.id || index} className="flex items-center justify-between p-2 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.nome || item.aluno}</p>
              {item.horario && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Clock className="h-3 w-3" />
                  {item.horario}
                </p>
              )}
              {item.vencimento && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Calendar className="h-3 w-3" />
                  {item.vencimento}
                </p>
              )}
              {item.professores && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <User className="h-3 w-3" />
                  {item.professores}
                </p>
              )}
            </div>
            <div className="ml-4 text-right">
              {item.valor && (
                <p className="text-sm font-semibold text-gray-900">R$ {item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              )}
              {item.alunos !== undefined && (
                <p className="text-xs text-gray-500">{item.alunos}/{item.limite} alunos</p>
              )}
              {item.ocupacao !== undefined && (
                <p className="text-xs text-gray-500">{item.ocupacao}% ocupação</p>
              )}
              {item.diasAtraso !== undefined && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {item.diasAtraso} dias
                </p>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Nenhum item encontrado
          </div>
        )}
      </div>
    </div>
  );
};

export default ListWidget;

